import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { SimulatorService } from '../../../../../pre-simulator/services/simulator/simulator.service';
import { ModalGenericComponent } from '../../../../../shared/components/modal-generic/modal-generic.component';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import {
  ES_PHONE_REGEXP,
  ES_POSTAL_CODE_REGEXP,
  MIN_STATUS,
  VALIDATORS_LENGTH,
} from '../../../../../shared/utils/constants';
import { ConfirmationService } from '../../../../../vass-core/components/modal/confirmation.service';
import { CommonService } from '../../../../../vass-core/services/common.service';
import { i18nService } from '../../../../../vass-core/services/i18n.service';
import { UploadFileComponent } from '../../../../components/modals/upload-file/upload-file.component';
import { AppraisalModel } from '../../../../models/appraisal.model';
import { SelectModel } from '../../../../models/select.model';
import { MortgageService } from '../../../../services/mortgage/mortgage.service';
import { mapAppraisal } from '../../appraisal.mapper';

@Component({
  selector: 'app-appraisal-form',
  templateUrl: './appraisal-form.component.html',
  styleUrls: ['./appraisal-form.component.scss'],
  standalone: false,
})
export class AppraisalFormComponent implements OnInit, OnChanges {
  @Output() emitHasAppraisal: EventEmitter<boolean> = new EventEmitter();
  @Output() emitAppraisalAmount: EventEmitter<number> = new EventEmitter();
  @Output() emitMortgageAppraisalData: EventEmitter<any> = new EventEmitter();
  @Output() emitMortgageAppraisalDataSave: EventEmitter<any> = new EventEmitter();
  @Output() emitMorgageAprraisalFile: EventEmitter<any> = new EventEmitter();
  @Output() emitUploadFile: EventEmitter<any> = new EventEmitter();

  @Input() appraisal: AppraisalModel;
  @Input() status: number;

  public HTML_VALIDATORS = VALIDATORS_LENGTH;
  public minStatus = MIN_STATUS;
  public appraisalForm: UntypedFormGroup;
  public hasAppraisal: boolean;
  public appraisalAmount: number;
  public localeDate;

  public currentValue = '';

  public listRoadType$: Observable<Array<SelectModel>>;
  public listApprTypeAssets$: Observable<Array<SelectModel>>;
  public listApprRegistry$: Observable<Array<SelectModel>>;

  public listCCAA: Array<any>;

  private appraisalCache: AppraisalModel = {} as AppraisalModel;

  public date = new Date();
  public configCalendar = {
    date: {
      yearRange: '',
      maxDate: new Date(
        this.date.getFullYear() - 18,
        this.date.getMonth(),
        this.date.getDate(),
      ),
      minDate: new Date(
        this.date.getFullYear() - 100,
        this.date.getMonth(),
        this.date.getDate() + 1,
      ),
    },
  };

  validatorsForm = {
    streetType: [],
    street: [
      Validators.minLength(this.HTML_VALIDATORS.minLength),
      Validators.maxLength(this.HTML_VALIDATORS.maxLength.roadName),
    ],
    number: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.roadNumber)],
    flat: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.floor)],
    door: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.door)],
    postalCode: [
      Validators.maxLength(5),
      Validators.minLength(5),
      Validators.pattern(ES_POSTAL_CODE_REGEXP),
    ],
    location: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.generic)],
    province: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.generic)],
    name: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.generic)],
    phone: [Validators.pattern(ES_PHONE_REGEXP)],
    otherLocation: [],
    reference: [Validators.maxLength(this.HTML_VALIDATORS.maxLength.reference)],
    typeAsset: [],
  };

  constructor(
    private formBuilder: UntypedFormBuilder,
    private confirmationService: ConfirmationService,
    private _i18n: i18nService,
    private authService: AuthService,
    private _simulatorService: SimulatorService,
    private translate: TranslateService,
    private _commonService: CommonService,
    private _mortgageService: MortgageService,
  ) {}

  ngOnInit() {
    this.confCalendar();
    this.getCCAA();
    this.initAppraisalForm();
    this.listRoadType$ = this._mortgageService.getRoadType();
    this.listApprTypeAssets$ = this._mortgageService.getApprTypeAssets();
    this.listApprRegistry$ = this._mortgageService.getApprRegistry();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.appraisal && changes.appraisal.currentValue && this.appraisalForm) {
      this.setHasApprisal();
      this.setAddressData();
      this.setCatastralData();
    }
  }

  private getCCAA() {
    this._simulatorService.getCCAA().subscribe(
      (data) => {
        this.listCCAA = data;
      },
      (err) => {},
    );
  }

  private setHasApprisal() {
    this.hasAppraisal = this.appraisal.mortgagesAppraisalData.appraisal;
  }

  private setAddressData() {
    this.appraisalForm.patchValue(mapAppraisal(this.appraisal));
  }

  private setCatastralData() {
    const catastralArray = <UntypedFormArray>this.appraisalForm.controls.catastralRefs;
    this.appraisal.mortgagesAppraisalData.cadastralData.forEach((cadastral) => {
      this.addReference();
    });
    if (this.appraisal.mortgagesAppraisalData.cadastralData.length === 0) {
      this.addReference();
    } else {
      this.appraisalForm
        .get('catastralRefs')
        .patchValue(this.appraisal.mortgagesAppraisalData.cadastralData);
    }
  }

  private confCalendar() {
    const fechaAux = new Date();
    (this.configCalendar.date.yearRange = `${fechaAux.getFullYear() - 100}:${
      fechaAux.getFullYear() - 18
    }`),
      this._i18n.getLiteral('web_config_calendar_object').then((resp) => {
        this.localeDate = JSON.parse(resp.replace(/'/g, '"'));
      });
  }

  private initAppraisalForm() {
    this.appraisalForm = this.formBuilder.group({
      streetType: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.streetType,
      ],
      street: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.street,
      ],
      number: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.number,
      ],
      flat: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.flat,
      ],
      door: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.door,
      ],
      postalCode: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.postalCode,
      ],
      location: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.location,
      ],
      province: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.province,
      ],
      catastralRefs: this.formBuilder.array([]),
      name: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.name,
      ],
      phone: [
        { value: '', disabled: this.status > this.minStatus.appraisal },
        this.validatorsForm.phone,
      ],
      otherLocation: [{ value: '', disabled: this.status > this.minStatus.appraisal }],
    });

    this.appraisalForm.controls.postalCode.setAsyncValidators(this.validatePostalCode());
  }

  public hasAppraisalChanged() {
    if (this.hasAppraisal) {
      this.appraisalCache = this.appraisalForm.value;
      this.appraisalForm.get('phone').disable();
      this.appraisalForm.get('name').disable();
      this.appraisalForm.get('phone').setValue('');
      this.appraisalForm.get('name').setValue('');
      for (const control in this.appraisalForm.controls) {
        this.appraisalForm.controls[control].markAsUntouched();
      }
    } else {
      this.appraisalAmount = null;
      if (this.status <= 7) {
        this.appraisalForm.enable();
        this.appraisalForm.patchValue(this.appraisalCache);
      }
    }
    this.emitHasAppraisal.emit(this.hasAppraisal);
  }

  public appraisalAmountChanged() {
    this.emitAppraisalAmount.emit(this.appraisalAmount);
  }

  public submitAppraisalForm(isSaving = false) {
    // Si se está guardando los campos no son obligatorios.
    if (isSaving) {
      this.setValidatorsRequired(false);
    } else {
      // Si se avanza el caso tiene que introducir los datos obligatoriamente.
      this.setValidatorsRequired(true);
    }

    // Guardar cuando adjuntas nota simple y el formulario es valido
    // if (this.appraisalForm.valid && isSaving) {
    //     this.emitMortgageAppraisalDataSave.emit(this.appraisalForm.value);
    //     return;
    // } else
    if (this.appraisalForm.valid) {
      this.emitMortgageAppraisalDataSave.emit(this.appraisalForm.value);
      // this.emitMortgageAppraisalData.emit(appraisalValue);
      return;
    } else if (this.appraisalForm.invalid) {
      this.markAsTouchedAllFields();
      return;
    }

    // Si está pendiente esperar a que resuelva
    const subscribeChanges = this.appraisalForm.statusChanges
      .pipe(
        filter((status) => status !== 'PENDING'),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        if (!this.appraisalForm.valid) {
          this.markAsTouchedAllFields();
          subscribeChanges.unsubscribe();
          return;
        }
        if (isSaving) {
          this.emitMortgageAppraisalDataSave.emit(this.appraisalForm.value);
        } else {
          this.emitMortgageAppraisalData.emit(this.appraisalForm.value);
        }
        subscribeChanges.unsubscribe();
      });
  }

  public addReference() {
    const catastralRefsArray = <UntypedFormArray>(
      this.appraisalForm.controls.catastralRefs
    );

    if (catastralRefsArray.length >= 10) {
      return;
    }

    catastralRefsArray.push(
      this.formBuilder.group({
        reference: [
          { value: '', disabled: this.status > 7 },
          this.validatorsForm.reference,
        ],
        typeAsset: [
          { value: '', disabled: this.status > 7 },
          this.validatorsForm.typeAsset,
        ],
        propertyRegistry: this.formBuilder.group({
          registry: [''],
          registryNumber: [''],
          farmNumber: [''],
          volume: [''],
          book: [''],
          folio: [''],
        }),
        idufir: [''],
      }),
    );
  }

  public deleteReference(index) {
    const catastralRefsArray = <UntypedFormArray>(
      this.appraisalForm.controls.catastralRefs
    );
    catastralRefsArray.removeAt(index);
  }

  public addAppraisalFile() {
    this.appraisalAmountChanged();
    this.emitMorgageAprraisalFile.emit();
  }

  private markAsTouchedAllFields() {
    for (const field in this.appraisalForm.controls) {
      if (field === 'catastralRefs') {
        this.markAsTouchedCatastralInputs(field);
      } else {
        this.appraisalForm.controls[field].markAsTouched();
      }
    }
  }

  private markAsTouchedCatastralInputs(catrastalRef) {
    const catForm = <UntypedFormGroup>this.appraisalForm.controls[catrastalRef];
    for (const catastralField in catForm.controls) {
      const catastralFormGroup = <UntypedFormGroup>catForm.controls[catastralField];
      catastralFormGroup.controls['reference'].markAsTouched();
      catastralFormGroup.controls['typeAsset'].markAsTouched();
    }
  }

  openModal(id: string) {
    const data = {
      message: this.translate.instant(`documentation.info_${id}`),
      txtButton: this.translate.instant('btn_aceptar'),
    };
    const options: Object = {
      header: this.translate.instant(`documentation.name_${id}`),
      showHeader: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'modal-class',
      cancelButton: false,
      acceptButton: false,
    };

    this.confirmationService.confirm(<any>{
      component: ModalGenericComponent,
      options: options,
      data: data,
      accept: (data) => {},
      reject: () => {},
    });
  }

  public openUploadFileModal() {
    const options: Object = {
      txtButton: '',
      showHeader: true,
      header: this.translate.instant('appraisal_form_upload_appraisal'),
      closeOnEscape: false,
      closable: true,
      styleClass: 'modal-class',
      cancelButton: false,
      acceptButton: false,
    };

    this.confirmationService.confirm(<any>{
      component: UploadFileComponent,
      options,
      accept: (data) => {},
      reject: () => {},
    });
  }

  public openToast(id: string) {
    const elementHTML = document.getElementById(id);
    elementHTML.focus();
  }

  openModalUpload() {
    this.emitUploadFile.emit();
  }

  setValidatorsRequired(required: boolean) {
    for (const campo in this.appraisalForm.controls) {
      let validatorsAux;
      if (this.validatorsForm[campo]) {
        validatorsAux = this.validatorsForm[campo] && this.validatorsForm[campo].slice(0);
      }
      if (
        required &&
        validatorsAux &&
        campo !== 'number' &&
        campo !== 'flat' &&
        campo !== 'door' &&
        campo !== 'otherLocation'
      ) {
        validatorsAux.push(Validators.required);
      }
      if (this.appraisalForm.controls[campo]) {
        if (campo === 'catastralRefs') {
          const catastralRefs: any = this.appraisalForm.controls['catastralRefs'];
          for (let i = 0; i < catastralRefs.controls.length; i++) {
            for (const catastralRef in catastralRefs.controls[i].controls) {
              validatorsAux =
                this.validatorsForm[catastralRef] &&
                this.validatorsForm[catastralRef].slice(0);
              if (required && validatorsAux) {
                validatorsAux.push(Validators.required);
              }
              catastralRefs.controls[i].controls[catastralRef].setValidators(
                validatorsAux,
              );
              catastralRefs.controls[i].controls[catastralRef].updateValueAndValidity();
            }
          }
        } else if (this.hasAppraisal && (campo === 'name' || campo === 'phone')) {
          // no finar como requerido el nombre de contacto y el telefono.
        } else {
          this.appraisalForm.controls[campo].setValidators(validatorsAux);
          this.appraisalForm.controls[campo].updateValueAndValidity();
        }
      }
    }
  }

  private validatePostalCode() {
    return ({ value }) => {
      if (
        value &&
        value !== '' &&
        value.match(ES_POSTAL_CODE_REGEXP) &&
        this.currentValue !== value
      ) {
        return this._mortgageService.validatePostalCode(value);
      } else {
        this.currentValue = value;
        return of(null);
      }
    };
  }
}
